import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { Plant, ViewMode } from '../types';
import { t } from '../services/i18n';
import { LayoutGrid, List, Search, MapPin, Filter, Loader2, Sprout, LayoutList, ArrowUpDown, Calendar, Tag, Activity, Book, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';

type SortKey = 'nomeComum' | 'nomeCientifico' | 'categoria' | 'dataAquisicao' | 'status';

/**
 * Skeleton Loader para os cards da coleção
 */
const CollectionSkeleton: React.FC<{ viewMode: ViewMode }> = ({ viewMode }) => {
  return (
    <div className={`
      ${viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : ''}
      ${viewMode === 'list' ? 'flex flex-col gap-4' : ''}
      ${viewMode === 'cards' ? 'flex flex-col gap-6' : ''}
    `}>
      {[...Array(6)].map((_, i) => (
        <div 
          key={i} 
          className={`bg-raizama-surface rounded-xl overflow-hidden border border-raizama-border/10 shadow-lg animate-pulse 
            ${viewMode === 'list' ? 'flex h-28' : ''}
            ${viewMode === 'cards' ? 'h-80 w-full' : ''}
          `}
        >
          <div className={`
            ${viewMode === 'list' ? 'w-28 h-full' : 'h-40 w-full'} 
            ${viewMode === 'cards' ? 'h-60 w-full' : ''}
            bg-white/5`} 
          />
          <div className="p-3 flex flex-col justify-center flex-1 gap-2">
            <div className="h-4 bg-white/5 rounded w-3/4" />
            <div className="h-3 bg-white/5 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente interno para gerenciar o estado de carregamento de cada imagem
const PlantImage: React.FC<{ src: string; alt: string; viewMode: ViewMode }> = ({ src, alt, viewMode }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative w-full h-full bg-raizama-surface flex items-center justify-center overflow-hidden">
      {/* Placeholder / Skeleton local da imagem */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-raizama-surface flex items-center justify-center animate-pulse">
          <Sprout className="text-raizama-border/20" size={viewMode === 'list' ? 32 : 48} />
        </div>
      )}
      
      {/* Imagem Real */}
      <img 
        src={src} 
        alt={alt} 
        onLoad={() => setIsLoaded(true)}
        className={`w-full h-full object-cover transition-opacity duration-700 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'} group-hover:scale-110 transition-transform duration-500`} 
      />
    </div>
  );
};

export const Collection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('nomeComum');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showSortOptions, setShowSortOptions] = useState(false);
  
  const [plantToDelete, setPlantToDelete] = useState<Plant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!plantToDelete) return;
    setIsDeleting(true);
    try {
      await storage.deletePlant(plantToDelete.id);
      setPlants(prev => prev.filter(p => p.id !== plantToDelete.id));
      setPlantToDelete(null);
    } catch (err) {
      console.error("Erro ao deletar planta:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  
  
  // ID da nova planta para animação de destaque
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlants = async () => {
      try {
        const data = await storage.getPlants();
        setPlants(data);
        
        // Verifica se há uma nova planta via estado da navegação
        const state = location.state as { newPlantId?: string } | null;
        if (state?.newPlantId) {
          setNewlyAddedId(state.newPlantId);
          // Limpa o estado da location para não repetir a animação em recargas
          window.history.replaceState({}, document.title);
          
          // Remove o destaque após alguns segundos
          setTimeout(() => setNewlyAddedId(null), 5000);
        }
      } catch (err) {
        console.error("Error fetching plants:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlants();
  }, [location]);

  const filteredAndSortedPlants = useMemo(() => {
    const filtered = plants.filter(p => {
      const searchLower = searchTerm.toLowerCase();
      return (
          p.nomeComum.toLowerCase().includes(searchLower) ||
          p.categoria.toLowerCase().includes(searchLower) ||
          (p.nomeCientifico && p.nomeCientifico.toLowerCase().includes(searchLower)) ||
          (p.genero && p.genero.toLowerCase().includes(searchLower)) ||
          (p.familia && p.familia.toLowerCase().includes(searchLower))
      );
    });

    return filtered.sort((a, b) => {
      let valA = a[sortKey] || '';
      let valB = b[sortKey] || '';

      if (sortKey === 'dataAquisicao') {
        const dateA = new Date(valA).getTime();
        const dateB = new Date(valB).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }

      const stringA = String(valA).toLowerCase();
      const stringB = String(valB).toLowerCase();

      if (sortOrder === 'asc') {
        return stringA.localeCompare(stringB);
      } else {
        return stringB.localeCompare(stringA);
      }
    });
  }, [plants, searchTerm, sortKey, sortOrder]);

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleSortChange = (key: SortKey) => {
    if (sortKey === key) {
      toggleSortOrder();
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
    setShowSortOptions(false);
  };

  const renderPlantItem = (plant: Plant, index: number) => {
    const isNew = plant.id === newlyAddedId;
    const highlightClass = isNew ? 'animate-highlight-pop border-raizama-action ring-4 ring-raizama-action/20 z-10' : '';

    if (viewMode === 'cards') {
      return (
        <motion.div 
          key={plant.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: index * 0.05 }}
          className={`bg-raizama-surface rounded-3xl overflow-hidden border border-raizama-border/20 hover:border-raizama-action transition-all cursor-pointer group shadow-2xl relative h-80 w-full mb-6 ${highlightClass}`}
          onClick={() => navigate(`/plant/${plant.id}`)}
        >
          <div className="absolute inset-0">
            <PlantImage src={plant.imagens?.[0]} alt={plant.nomeComum} viewMode={viewMode} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col justify-end">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-raizama-action text-white text-[10px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest shadow-lg">
                {plant.categoria}
              </span>
              <span className="bg-black/40 backdrop-blur-md text-raizama-textSec text-[10px] font-bold px-2.5 py-1 rounded-lg border border-white/10">
                {plant.status}
              </span>
              {isNew && (
                <span className="bg-yellow-500 text-black text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse">{t('coll_new_tag')}</span>
              )}
            </div>
            <h4 className="font-black text-white text-xl uppercase tracking-tighter leading-none">{plant.nomeComum}</h4>
            <p className="text-xs text-raizama-textSec italic opacity-80 mt-1">{plant.nomeCientifico}</p>
          </div>

          <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={(e) => { e.stopPropagation(); setPlantToDelete(plant); }}
              className="bg-black/50 hover:bg-red-600/80 backdrop-blur-md p-2 rounded-xl text-white transition-colors"
              title="Excluir"
            >
              <Trash2 size={18} />
            </button>
            <div className="bg-black/50 backdrop-blur-md p-2 rounded-xl text-white">
              <MapPin size={18} />
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div 
          key={plant.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: index * 0.05 }}
          className={`bg-raizama-surface rounded-xl overflow-hidden border border-raizama-border/20 hover:border-raizama-action transition-all cursor-pointer group shadow-lg ${viewMode === 'list' ? 'flex h-28' : ''} ${highlightClass}`}
          onClick={() => navigate(`/plant/${plant.id}`)}
      >
          <div className={`${viewMode === 'list' ? 'w-28 h-full' : 'h-40 w-full'} bg-gray-800 relative overflow-hidden`}>
              <PlantImage 
                src={plant.imagens?.[0]} 
                alt={plant.nomeComum} 
                viewMode={viewMode}
              />
               <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded text-[10px] text-white font-bold z-10">
                  {plant.categoria}
               </div>
               {isNew && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-black text-[8px] font-black px-2 py-0.5 rounded-full uppercase z-10 animate-bounce">{t('coll_recent_tag')}</div>
               )}
               <button 
                 onClick={(e) => { e.stopPropagation(); setPlantToDelete(plant); }}
                 className="absolute bottom-2 right-2 bg-black/60 hover:bg-red-600/80 backdrop-blur-sm p-1.5 rounded text-white z-10 opacity-0 group-hover:opacity-100 transition-all"
                 title="Excluir"
               >
                 <Trash2 size={14} />
               </button>
          </div>
          
          <div className="p-3 flex flex-col justify-center flex-1">
              <h4 className="font-bold text-white text-sm truncate uppercase tracking-tight">{plant.nomeComum}</h4>
              <p className="text-xs text-raizama-textSec italic truncate">{plant.nomeCientifico}</p>
              
              {viewMode === 'list' && (
                  <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 text-[10px] text-raizama-highlight">
                          <MapPin size={10} />
                          {plant.localizacao?.descricao || plant.origem}
                      </div>
                  </div>
              )}
          </div>
      </motion.div>
    );
  };

  const sortOptions: { key: SortKey; label: string; icon: React.ReactNode }[] = [
    { key: 'nomeComum', label: t('sort_name_common'), icon: <Sprout size={14} /> },
    { key: 'nomeCientifico', label: t('sort_name_scientific'), icon: <Book size={14} /> },
    { key: 'categoria', label: t('sort_category'), icon: <Tag size={14} /> },
    { key: 'dataAquisicao', label: t('sort_date'), icon: <Calendar size={14} /> },
    { key: 'status', label: t('sort_status'), icon: <Activity size={14} /> },
  ];

  return (
    <div className="flex flex-col h-full relative">
      <div className="flex flex-col gap-4 mb-6">
        <div className="relative">
            <Search className="absolute left-3 top-3 text-raizama-textSec" size={20} />
            <input 
                type="text"
                placeholder={t('coll_search_placeholder')}
                className="w-full bg-raizama-surface border border-raizama-border/30 rounded-full pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-raizama-action transition-all placeholder:text-gray-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button 
              onClick={() => setShowSortOptions(!showSortOptions)}
              className={`absolute right-3 top-2.5 p-0.5 rounded-lg transition-colors ${showSortOptions ? 'text-raizama-action' : 'text-raizama-textSec'}`}
            >
              <ArrowUpDown size={20} />
            </button>
        </div>

        {/* Painel de Ordenação */}
        {showSortOptions && (
          <div className="bg-raizama-surface/90 backdrop-blur-md border border-raizama-border/30 rounded-2xl p-4 animate-scale-up shadow-2xl">
            <h5 className="text-[10px] font-black text-raizama-highlight uppercase tracking-widest mb-3 px-1">{t('coll_sort_by')}</h5>
            <div className="grid grid-cols-1 gap-2">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => handleSortChange(opt.key)}
                  className={`flex items-center justify-between p-3 rounded-xl text-xs transition-all ${sortKey === opt.key ? 'bg-raizama-action text-white' : 'bg-black/20 text-raizama-textSec hover:bg-black/40'}`}
                >
                  <div className="flex items-center gap-3">
                    {opt.icon}
                    <span className="font-bold">{opt.label}</span>
                  </div>
                  {sortKey === opt.key && (
                    <span className="text-[10px] font-black uppercase">
                      {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
            <div className="bg-raizama-surface rounded-xl p-1 flex gap-1 border border-raizama-border/20 shadow-inner">
                 <button 
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-raizama-action text-white shadow-lg scale-105' : 'text-raizama-textSec hover:text-white'}`}
                    title="Grade"
                 >
                    <LayoutGrid size={18} />
                 </button>
                 <button 
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-raizama-action text-white shadow-lg scale-105' : 'text-raizama-textSec hover:text-white'}`}
                    title="Lista"
                 >
                    <List size={18} />
                 </button>
                 <button 
                    onClick={() => setViewMode('cards')}
                    className={`p-1.5 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-raizama-action text-white shadow-lg scale-105' : 'text-raizama-textSec hover:text-white'}`}
                    title="Cartões Imersivos"
                 >
                    <LayoutList size={18} />
                 </button>
            </div>
            {!loading && (
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-raizama-textSec font-black uppercase tracking-widest bg-black/20 px-3 py-1 rounded-full border border-white/5">
                  {filteredAndSortedPlants.length} {t('coll_plants_count')}
                </span>
                <span className="text-[8px] text-raizama-action font-bold uppercase mt-1 px-1">
                  {t('coll_organized_by')} {sortOptions.find(o => o.key === sortKey)?.label}
                </span>
              </div>
            )}
            {loading && <div className="h-6 w-16 bg-white/5 rounded-full animate-pulse" />}
        </div>
      </div>

      {loading ? (
        <CollectionSkeleton viewMode={viewMode} />
      ) : filteredAndSortedPlants.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 opacity-60">
            <Search size={32} className="text-raizama-textSec mb-4" />
            <p className="text-raizama-textMain font-medium">{t('coll_empty')}</p>
        </div>
      ) : (
        <div className={`
          ${viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : ''}
          ${viewMode === 'list' ? 'flex flex-col gap-4' : ''}
          ${viewMode === 'cards' ? 'flex flex-col' : ''}
        `}>
            {filteredAndSortedPlants.map((plant, index) => renderPlantItem(plant, index))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {plantToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-raizama-surface border border-raizama-border/20 p-8 rounded-[2rem] w-full max-w-sm text-center shadow-2xl animate-scale-up">
            <div className="w-16 h-16 bg-red-600/10 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
              <Trash2 size={32} />
            </div>
            <h3 className="text-xl font-black text-white mb-2 uppercase tracking-tight">Confirmar Exclusão</h3>
            <p className="text-xs text-raizama-textSec mb-8 leading-relaxed">
              {t('details_delete_confirm_desc').replace('{{count}}', (plantToDelete.imagens?.length || 0).toString()).replace('{{name}}', plantToDelete.nomeComum)}
            </p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={handleDeleteConfirm} 
                disabled={isDeleting}
                className="w-full py-4 bg-red-600 text-white rounded-2xl font-black hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest shadow-lg shadow-red-600/20"
              >
                {isDeleting ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={16} />}
                {t('details_delete_confirm_btn')}
              </button>
              <button 
                onClick={() => setPlantToDelete(null)} 
                disabled={isDeleting}
                className="w-full py-4 bg-white/5 border border-white/10 text-white font-bold rounded-2xl hover:bg-white/10 transition-colors uppercase text-[10px] tracking-widest"
              >
                {t('details_delete_cancel_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};